import { useEffect } from "react";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { useNavigate, useRouteError } from "react-router-dom";

const getErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (typeof error === "object" && error && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" ? message : null;
  }

  return null;
};

export default function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const errorMessage = getErrorMessage(error);

  // Automatically handle chunk loading / dynamic import errors by forcing a reload
  useEffect(() => {
    if (errorMessage) {
      const isChunkError = 
        errorMessage.includes("Failed to fetch dynamically imported module") ||
        errorMessage.includes("ChunkLoadError") ||
        errorMessage.includes("Failed to load module script") ||
        errorMessage.toLowerCase().includes("mime type");

      if (isChunkError) {
        console.warn("Dynamic import failed (possibly due to redeployment). Reloading page...", errorMessage);
        window.location.reload();
      }
    }
  }, [errorMessage]);

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: "var(--color-background)" }}
    >
      <div className="text-center px-6 max-w-2xl">
        <div
          className="w-24 h-24 rounded-2xl mx-auto mb-6 flex items-center justify-center"
          style={{ backgroundColor: "var(--color-danger-light)" }}
        >
          <AlertTriangle className="w-12 h-12" style={{ color: "var(--color-danger-600)" }} />
        </div>

        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--color-text-primary)" }}>
          Oops! Something went wrong
        </h1>

        <p className="text-lg mb-2" style={{ color: "var(--color-text-tertiary)" }}>
          We encountered an unexpected error. Please try again.
        </p>

        {errorMessage ? (
          <p
            className="text-sm mb-8 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: "var(--color-danger-light)",
              color: "var(--color-danger-700)",
            }}
          >
            {errorMessage}
          </p>
        ) : null}

        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-6 py-2.5 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Reload Page
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Home className="w-4 h-4" />
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
